import { type PropsWithChildren } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/useAuth';
import { Button } from '@/components/ui/button';

function SignInRequired() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <div className="max-w-lg space-y-4 text-center">
        <h1 className="text-xl font-semibold text-foreground">Sign in required</h1>
        <p className="text-sm text-muted-foreground">
          Sign in with Google to open the Rodeo Nails management app.
        </p>
        <Button asChild>
          <Link to="/auth">Go to sign in</Link>
        </Button>
      </div>
    </div>
  );
}

function AccessPending({ email, onSignOut }: { email?: string | null; onSignOut: () => void }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <div className="max-w-md space-y-4 text-center">
        <h1 className="text-xl font-semibold text-foreground">Access not granted</h1>
        <p className="text-sm text-muted-foreground">
          {email ? (
            <>
              Signed in as <span className="font-medium text-foreground">{email}</span>, but this
              account does not have admin access yet.
            </>
          ) : (
            'Your account does not have admin access yet.'
          )}
        </p>
        <p className="text-sm text-muted-foreground">
          Ask an operator to run{' '}
          <code className="rounded bg-muted px-1.5 py-0.5 text-xs">npm run grant-admin</code> with
          your email or Firebase Auth UID, then sign out and sign in again.
        </p>
        <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-center">
          <Button type="button" variant="outline" onClick={onSignOut}>
            Sign out
          </Button>
          <Button asChild variant="default">
            <Link to="/auth">Back to sign in</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

export function AdminAccessGate({ children }: PropsWithChildren) {
  const { user, isLoadingAuth, isAdmin, logout } = useAuth();

  if (isLoadingAuth) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6">
        <div className="text-center">
          <p className="text-sm font-medium text-muted-foreground">Checking sign-in...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <SignInRequired />;
  }

  if (!isAdmin) {
    return (
      <AccessPending
        email={user.email}
        onSignOut={() => {
          void logout().then(() => {
            window.location.assign('/auth');
          });
        }}
      />
    );
  }

  return children;
}
